<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import MediaManagerModal from '@/components/organisms/MediaManagerModal.vue'
import BetaContentPanel from '@/components/beta-dashboard/BetaContentPanel.vue'
import BetaAffiliatesPanel from '@/components/beta-dashboard/organisms/BetaAffiliatesPanel.vue'
import BetaConfirmationModal from '@/components/beta-dashboard/organisms/BetaConfirmationModal.vue'
import BetaDashboardLayout from '@/components/beta-dashboard/BetaDashboardLayout.vue'
import BetaDashboardOverview from '@/components/beta-dashboard/BetaDashboardOverview.vue'
import BetaHelpPanel from '@/components/beta-dashboard/organisms/BetaHelpPanel.vue'
import BetaOpportunitiesPanel from '@/components/beta-dashboard/organisms/BetaOpportunitiesPanel.vue'
import BetaSubscriptionsPanel from '@/components/beta-dashboard/organisms/BetaSubscriptionsPanel.vue'
import BetaSettingsPanel from '@/components/beta-dashboard/BetaSettingsPanel.vue'
import BetaLayoutPanel from '@/components/beta-dashboard/BetaLayoutPanel.vue'
import BetaCreateContentDialog from '@/components/beta-dashboard/organisms/BetaCreateContentDialog.vue'
import BetaPageSettingsDialog from '@/components/beta-dashboard/organisms/BetaPageSettingsDialog.vue'
import BetaModalLayer from '@/components/beta-dashboard/organisms/BetaModalLayer.vue'
import modal from '@/services/modal'
import notification, { type ToastId } from '@/services/notification'
import { finalizeBetaDashboardSession } from '@/services/beta-dashboard'
import { useBetaDashboardStore } from '@/stores/beta-dashboard'
import { useApplicationStore } from '@/stores/application'
import { useMakePagesStore } from '@/stores/modal/make-pages'
import { useSiteStore } from '@/stores/site'
import type {
    BetaAiSuggestion,
    BetaCreatePageKind,
    BetaCreatePageSelection,
    BetaDashboardTask,
    BetaDashboardView,
    BetaPage,
    BetaPageCombinationGroups,
    BetaSettingsSection,
} from '@/types/beta-dashboard'
import {
    betaCreatePageKindUnavailable,
    buildBetaCreatePageInput,
} from '@/utils/beta-page-creation'
import { betaPanelQuery } from '@/utils/panel-variant'
import {
    betaSettingsSectionForAdvancedRoute,
    type BetaAdvancedSettingsRouteName,
} from '@/utils/beta-settings-navigation'
import { isExternalLinkPage } from '@/utils/page'

const route = useRoute()
const router = useRouter()
const betaStore = useBetaDashboardStore()
const applicationStore = useApplicationStore()
const makePagesStore = useMakePagesStore()
const siteStore = useSiteStore()

const creatingPage = ref(false)
const creatingPost = ref(false)
const loggingOut = ref(false)
const createDialogOpen = ref(false)
const createDialogKind = ref<BetaCreatePageKind>()
const pageForSettings = ref<BetaPage | null>(null)

const isOldAppearanceRoute = computed(() => {
    const view = Array.isArray(route.query.view) ? route.query.view[0] : route.query.view
    const settings = Array.isArray(route.query.settings) ? route.query.settings[0] : route.query.settings
    return view === 'settings' && settings === 'appearance'
})

watch(isOldAppearanceRoute, (isOldRoute) => {
    if (!isOldRoute) return

    void router.replace({
        name: 'dashboard',
        query: { ...route.query, ...betaPanelQuery(), view: 'layout', settings: undefined },
    })
}, { immediate: true })

const activeView = computed<BetaDashboardView>(() => {
    if (isOldAppearanceRoute.value) return 'layout'

    const value = Array.isArray(route.query.view) ? route.query.view[0] : route.query.view
    if (
        (value === 'affiliates' || value === 'subscriptions')
        && applicationStore.className === 'epics'
    ) return 'home'

    return value === 'content'
        || value === 'opportunities'
        || value === 'affiliates'
        || value === 'subscriptions'
        || value === 'help'
        || value === 'settings'
        || value === 'layout'
        ? value
        : 'home'
})

const settingsSection = computed<BetaSettingsSection>(() => {
    const value = Array.isArray(route.query.settings)
        ? route.query.settings[0]
        : route.query.settings

    return [
        'identity',
        'contact',
        'integrations',
        'domains',
        'seo',
        'advertising',
        'link-building',
        'redirects',
        'files',
    ].includes(String(value))
        ? value as BetaSettingsSection
        : 'identity'
})

const selectedPageId = computed(() => {
    const value = Array.isArray(route.query.page) ? route.query.page[0] : route.query.page
    if (!value) return null

    const id = Number(value)
    return Number.isFinite(id) ? id : null
})

const combinationGroups = computed<BetaPageCombinationGroups>(() => {
    const combinations = makePagesStore.combinations
    const groups = combinations && typeof combinations === 'object' ? combinations : {}

    return {
        ...groups,
        default: Array.isArray(makePagesStore.combinationsDefault)
            ? makePagesStore.combinationsDefault
            : [],
    }
})

const modeledKinds: BetaCreatePageKind[] = [
    'text',
    'gallery',
    'portfolio',
    'blog',
    'about',
    'contact',
    'testimonials',
    'newsletter',
    'store',
]

const unavailableKinds = computed<BetaCreatePageKind[]>(() => (
    makePagesStore.loadingCombinations
        ? modeledKinds
        : modeledKinds.filter((kind) => (
            betaCreatePageKindUnavailable(kind, combinationGroups.value)
        ))
))

const siteUrl = computed(() => {
    if (!siteStore.url) return null
    return /^https?:\/\//.test(String(siteStore.url))
        ? String(siteStore.url)
        : `https://${siteStore.url}`
})

const replaceDashboardQuery = (query: Record<string, string | number | null | undefined>) => {
    const cleaned = Object.fromEntries(
        Object.entries(query).filter(([, value]) => value !== undefined),
    )

    return router.push({
        name: 'dashboard',
        query: {
            ...betaPanelQuery(),
            ...cleaned,
        },
    })
}

const navigate = (view: BetaDashboardView) => {
    if (view === 'home') {
        replaceDashboardQuery({})
        return
    }

    replaceDashboardQuery({ view })
}

const openSettings = (section: BetaSettingsSection) => {
    replaceDashboardQuery({
        view: 'settings',
        settings: section,
    })
}

const openAdvancedSettings = (routeName: BetaAdvancedSettingsRouteName) => {
    openSettings(betaSettingsSectionForAdvancedRoute(routeName))
}

const selectPage = (page: BetaPage | null) => {
    replaceDashboardQuery({
        view: 'content',
        page: page?.id,
    })
}

const editPage = (page: BetaPage | null | undefined) => {
    if (!page?.id) {
        notification.simple('Não foi possível identificar a página.', 'error')
        return
    }

    if (isExternalLinkPage(page)) {
        router.push({
            name: 'page.external.edit',
            params: { id: page.id },
            query: betaPanelQuery(),
        })
        return
    }

    router.push({
        name: 'page.edit',
        params: { id: page.id },
        query: betaPanelQuery(),
    })
}

const editHome = () => {
    editPage(betaStore.homePage)
}

const editAbout = () => {
    editPage(betaStore.aboutPage)
}

const openClassic = () => {
    router.push({ name: 'dashboard' })
}

const logout = async () => {
    if (loggingOut.value) return

    loggingOut.value = true

    try {
        await finalizeBetaDashboardSession(Number(siteStore.id))

        const baseDomain = String(applicationStore.appBaseDomain || '').trim()
        localStorage.clear()

        if (!baseDomain) {
            await router.push('/login')
            return
        }

        const destination = baseDomain.includes('epics.vc')
            ? 'https://epics.com.br/dashboard'
            : /^https?:\/\//i.test(baseDomain)
                ? baseDomain
                : `https://${baseDomain}`

        window.location.replace(destination)
    } catch (error) {
        notification.simple(
            error instanceof Error ? error.message : 'Não foi possível sair agora.',
            'error',
        )
    } finally {
        loggingOut.value = false
    }
}

const editPost = (postId: number) => {
    router.push({
        name: 'post.edit',
        params: { id: postId },
        query: betaPanelQuery(),
    })
}

const openCreateDialog = (kind?: BetaCreatePageKind) => {
    createDialogKind.value = kind
    createDialogOpen.value = true
}

const openSidebarPage = (page: BetaPage) => {
    if (page.listPages == 1) {
        selectPage(page)
        return
    }

    editPage(page)
}

const openPageSettings = (page: BetaPage) => {
    pageForSettings.value = page
}

const handleDeletedPage = (pageId: number) => {
    if (selectedPageId.value === pageId) {
        selectPage(null)
    }
}

const createPage = async (payload: BetaCreatePageSelection) => {
    const input = buildBetaCreatePageInput(payload)

    if (!input) {
        notification.simple('Os modelos necessários ainda não foram carregados.', 'warning')
        return
    }

    creatingPage.value = true
    const toastId: ToastId = notification.loading('Criando página…')

    try {
        const page = await betaStore.createPage(input)
        createDialogOpen.value = false
        notification.update(toastId, 'Página criada com sucesso!', 'success')

        if (page.listPages == 1 || input.type === 'blog') {
            await betaStore.fetchPostsForPage(page.id, true)
            selectPage(page)
            return
        }

        editPage(page)
    } catch (error) {
        notification.update(
            toastId,
            error instanceof Error ? error.message : 'Não foi possível criar a página.',
            'error',
        )
    } finally {
        creatingPage.value = false
    }
}

const createPost = async (pageId: number) => {
    creatingPost.value = true
    const toastId: ToastId = notification.loading('Criando publicação…')

    try {
        const postId = await betaStore.createPost(pageId)
        notification.update(toastId, 'Publicação criada!', 'success')
        editPost(postId)
    } catch (error) {
        notification.update(
            toastId,
            error instanceof Error ? error.message : 'Não foi possível criar a publicação.',
            'error',
        )
    } finally {
        creatingPost.value = false
    }
}

const preview = (markReviewed = false) => {
    if (!siteUrl.value) {
        notification.simple('O site ainda não possui um endereço disponível.', 'warning')
        return
    }

    window.open(siteUrl.value, '_blank', 'noopener,noreferrer')

    if (markReviewed) {
        betaStore.markReviewed()
    }
}

const openStore = async () => {
    const storeWindow = window.open('about:blank', '_blank')
    if (storeWindow) storeWindow.opener = null

    try {
        const url = await betaStore.getStoreUrl()

        if (storeWindow) {
            storeWindow.location.href = url
        } else {
            window.open(url, '_blank', 'noopener,noreferrer')
        }
    } catch (error) {
        storeWindow?.close()
        notification.simple(
            error instanceof Error ? error.message : 'Não foi possível abrir a loja.',
            'error',
        )
    }
}

const handleTask = (task: BetaDashboardTask) => {
    switch (task.action) {
        case 'identity':
            openSettings('identity')
            break
        case 'home':
            editHome()
            break
        case 'contact':
            openSettings('contact')
            break
        case 'about':
            editAbout()
            break
        case 'content':
            navigate('content')
            break
        case 'preview':
            preview(true)
            break
        case 'domain':
            openSettings('domains')
            break
        case 'reload':
            betaStore.load()
            break
        case 'classic':
            openClassic()
            break
    }
}

const handleSuggestion = (suggestion: BetaAiSuggestion) => {
    switch (suggestion.action) {
        case 'identity':
            openSettings('identity')
            break
        case 'home':
            editHome()
            break
        case 'about':
            editAbout()
            break
        case 'contact':
            openSettings('contact')
            break
        case 'content': {
            const page = suggestion.pageId
                ? betaStore.pages.find((item) => item.id === suggestion.pageId)
                : null
            selectPage(page ?? null)
            break
        }
    }
}

const confirmDeleteDomain = async (domainId: number) => {
    const confirmed = await modal.confirmation(
        'Remover domínio?',
        'O domínio deixará de aparecer neste painel. Confirme apenas se ele não deve mais apontar para este site.',
        {
            type: 'danger',
            textConfirm: 'Sim, remover',
            textCancel: 'Cancelar',
        },
    )

    if (!confirmed) return

    try {
        await betaStore.removeDomain(domainId)
        notification.simple('Domínio removido.', 'success')
    } catch (error) {
        notification.simple(
            error instanceof Error ? error.message : 'Não foi possível remover o domínio.',
            'error',
        )
    }
}

onBeforeMount(async () => {
    await Promise.all([
        betaStore.load(),
        makePagesStore.fetchCombinations(),
    ])
})
</script>

<template>
    <BetaDashboardLayout
        :active-view="activeView"
        :store-loading="betaStore.loadingStore"
        :pages="betaStore.pages"
        :selected-page-id="selectedPageId"
        :unavailable-kinds="unavailableKinds"
        :creating-post="creatingPost"
        :logging-out="loggingOut"
        :settings-section="settingsSection"
        @navigate="navigate"
        @open-store="openStore"
        @select-page="openSidebarPage"
        @configure-page="openPageSettings"
        @create-page="openCreateDialog"
        @edit-home="editHome"
        @create-post="createPost"
        @open-settings="openSettings"
        @open-advanced-settings="openAdvancedSettings"
        @logout="logout"
    >
        <BetaDashboardOverview
            v-if="activeView === 'home'"
            @navigate="navigate"
            @preview="preview()"
            @edit-post="editPost"
            @task="handleTask"
            @suggestion="handleSuggestion"
        />

        <BetaContentPanel
            v-else-if="activeView === 'content'"
            :selected-page-id="selectedPageId"
            :creating-post="creatingPost"
            @select-page="selectPage"
            @edit-page="editPage"
            @configure-page="openPageSettings"
            @edit-post="editPost"
            @open-create="openCreateDialog"
            @create-post="createPost"
        />

        <BetaOpportunitiesPanel v-else-if="activeView === 'opportunities'" />

        <BetaAffiliatesPanel v-else-if="activeView === 'affiliates'" />

        <BetaSubscriptionsPanel v-else-if="activeView === 'subscriptions'" />

        <BetaHelpPanel v-else-if="activeView === 'help'" />

        <BetaLayoutPanel v-else-if="activeView === 'layout'" />

        <BetaSettingsPanel
            v-else
            :section="settingsSection"
            @section="openSettings"
            @confirm-delete-domain="confirmDeleteDomain"
        />
    </BetaDashboardLayout>

    <BetaCreateContentDialog
        :open="createDialogOpen"
        :loading="creatingPage"
        :existing-names="betaStore.pages.map((page) => page.name)"
        :combinations="combinationGroups"
        :unavailable-kinds="unavailableKinds"
        :initial-kind="createDialogKind"
        @close="createDialogOpen = false"
        @create="createPage"
    />

    <BetaPageSettingsDialog
        :page="pageForSettings"
        @close="pageForSettings = null"
        @deleted="handleDeletedPage"
    />

    <BetaModalLayer>
        <MediaManagerModal />
        <BetaConfirmationModal />
    </BetaModalLayer>
</template>
